'use client'

import { useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js'
import { subscribeToNotificationInvalidations } from '@/lib/supabase/browserRealtime'

const noticeSchema = z.object({
  revision_id: z.string().regex(/^[1-9][0-9]*$/),
  season_id: z.uuid(),
  gameweek_id: z.uuid(),
  gameweek_number: z.number().int().positive(),
  decision: z.enum(['updated', 'late_schedule_conflict', 'safety_hold', 'missing_fixture']),
  title: z.string(),
  message: z.string(),
  old_deadline: z.string().nullable(),
  proposed_deadline: z.string().nullable(),
  effective_deadline: z.string().nullable(),
  notification_id: z.uuid().nullable()
})
const responseSchema = z.object({ notices: z.array(noticeSchema) })

export type DeadlineNotice = z.infer<typeof noticeSchema>
export const deadlineNoticesKey = (userId: string | null, seasonId: string | null) =>
  ['deadlineNotices', userId, seasonId] as const

export function useDeadlineNotices(userId: string | null, seasonId: string | null) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => deadlineNoticesKey(userId, seasonId), [userId, seasonId])
  const query = useQuery({
    queryKey,
    enabled: !!userId && !!seasonId,
    queryFn: async (): Promise<DeadlineNotice[]> => {
      const response = await fetch(
        `/api/gameweeks/deadline-notices?${new URLSearchParams({ season_id: seasonId! })}`,
        { cache: 'no-store' }
      )
      if (!response.ok) throw new Error('Deadline notices are temporarily unavailable.')
      const parsed = responseSchema.parse(await response.json())
      if (parsed.notices.some((notice) => notice.season_id !== seasonId)) {
        throw new Error('Deadline notice scope changed.')
      }
      return parsed.notices
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true
  })

  useEffect(() => {
    if (!userId || !seasonId) return
    const refreshSchedule = () => {
      void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries({ queryKey: ['navigationSnapshot', userId] })
    }
    return subscribeToNotificationInvalidations({
      userId,
      nonce: crypto.randomUUID(),
      onInsert: (payload) => {
        if ('notification_type' in payload.new &&
            payload.new.notification_type === 'gameweek_deadline') refreshSchedule()
      },
      onUpdate: (payload) => {
        if ('notification_type' in payload.new &&
            payload.new.notification_type === 'gameweek_deadline') refreshSchedule()
      },
      onStatus: (status) => {
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) refreshSchedule()
      }
    })
  }, [userId, seasonId, queryClient, queryKey])

  const acknowledgment = useMutation({
    mutationFn: async (revisionId: string) => {
      const response = await fetch('/api/gameweeks/deadline-notices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revision_id: revisionId })
      })
      if (!response.ok) throw new Error('Unable to acknowledge the deadline notice.')
      const result = z.object({
        acknowledged: z.literal(true),
        revision_id: z.string()
      }).parse(await response.json())
      if (result.revision_id !== revisionId) {
        throw new Error('The deadline notice changed.')
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
  })

  return {
    notices: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    acknowledge: acknowledgment.mutateAsync,
    isAcknowledging: acknowledgment.isPending,
    refetch: query.refetch
  }
}
